<template>
    <div data-testid="data-centre-report" class="all-height all-width">
        <div class="pa-6 pb-16 d-flex flex-column gap-6">

            <ReportHeader 
            title="Data Centre Report" 
            test-id-prefix="data-centre-report" 
            @refresh="fetchInitialData" />

            <div class="d-flex flex-column gap-6">
                <ReportFilter 
                apply-button-text="Generate" 
                cycletype="Actual" 
                :showSync="false" 
                :showFavourite="false"
                :showperiod="false" 
                :showrole="false"
                :shownetwork="false" 
                :showregion="false" 
                :showcountry="false"
                :showreportingunit="false" 
                :showrutype="false"
                :showbatchtype="true"
                :showmultibatchtype="true"
                :showdatacentrestatus="true"
                :showmultidatacentrestatus="true"
                :showstartdate="true"
                :showenddate="true"
                :defaultStartDate="todayDate"
                :defaultEndDate="todayDate"
                :showReset="true"
                @newdata="handleFilterData" />
            </div>
            
            <ReportTable 
            :loading="getDataCentreReportListLoading" 
            :headers="getDataCentreReportHeader"
            :items="formattedDataCentreData" 
            :total-pages="getDataCentreReportTotalPages"
            :current-page="currentPage" 
            :items-per-page="itemsPerPage" 
            test-id-prefix="data-centre-report"
            :shimmer-slots="shimmerSlots"
            :data-slots="dataSlots" 
            @page-change="handlePageChange"
            @download="downloadFile" 
            @view-details="showDetails"
            @filter-change="handleTableFilterChange">
             <template v-slot:indexno="{ item }">
                    <div>
                        {{ item.serial_number }}
                    </div>
                </template>
             <template v-slot:data_context="{ item }">
                    <div>
                     {{ formatBatchTypes(item.filter && item.filter.request_type) || item.data_context || '-' }}
                    </div>
                </template>
            </ReportTable>
        </div>

        <ReportDialog 
        v-model="detailsDialog" 
        v-if="detailsDialog && detailsItem" 
        title="Data Centre"
        :details-item="detailsItem" 
        :show-ru-type="false"
        test-id-prefix="data-centre-report-dialog">
        <template v-slot:header-fields="{ item }">
                <!-- <div>{{item}}</div> -->
                <!-- Generated Time (Data Context) -->
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Generated Time</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ item.updated_at || '-' }}
                    </v-col>
                </v-row>

                <!-- Data Type -->
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Data Type</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ item.data_type || '-' }}
                    </v-col>
                </v-row>

                <!-- Period -->
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Period</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        <v-chip
                            v-if="item.period_range"
                            label
                            small
                            class="chip-choose"
                            data-testid="data-centre-report-dialog__period-chip"
                        >
                            <span class="categorystype">{{ item.period_range }}</span>
                        </v-chip>
                        <span v-else>-</span>
                    </v-col>
                </v-row>
                <!-- Approval Status -->
                <v-row no-gutters class="mb-2 align-center">
                    <v-col cols="12" sm="4" class="details-label">Status</v-col>
                    <v-col cols="12" sm="8" class="details-value font-weight-semibold">
                        {{ getApprovalStatusLabel(item.approval_status) }}
                    </v-col>
                </v-row>
            </template>
        </ReportDialog>
        
    </div>
</template>
<script>
import ReportHeader from './components/ReportHeader.vue';
import ReportTable from './components/ReportTable.vue';
import ReportDialog from './components/ReportDialog.vue';
import { STATUS_VARIANTS } from '@/modules/varianceanalysis/utils/statusConstants.js';
import { PAGINATION, REPORTS_API, MONTHS, FILTER_BATCH_TYPE_LIST, FILTER_DATA_CENTRE_STATUS_LIST } from '@/modules/varianceanalysis/utils/constants.js';
import { mapActions, mapGetters } from 'vuex';
import ReportFilter from '../../views/ReportFilter.vue';

export default {
    name: 'DataCentreReport',

    components: {
        ReportFilter,
        ReportHeader,
        ReportTable,
        ReportDialog
    },

    data() {
        return {
            selectedFilter: {},
            cycletype: 'Actual',
            filterLoading: false,
            filterList: {},
            detailsDialog: false,
            detailsItem: null,
            fetchTimeout: null,
            isInitialLoad: true, // Track if this is the first load
            currentPage: 1,
            itemsPerPage: PAGINATION.DEFAULT_LIMIT,
            statusFilter: [],
            dataSlots: ['indexno', 'data_context', 'created_at', 'status', 'action'],
            shimmerSlots: ['indexno', 'data_context', 'created_at', 'status', 'action'],
        };
    },

    mounted() {
        // Initial data fetch is deferred to handleFilterData when ReportFilter
        // signals loading complete (key='loading', value=false).
    },

    computed: {
        todayDate() {
            const d = new Date();
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },
        ...mapGetters('varianceanalysis/reports', [
            'getDataCentreReportData',
            'getDataCentreReportListLoading',
            'getDataCentreReportHeader',
            'getDataCentreReportTotalPages',
        ]),

        /**
         * Generates shimmer/skeleton placeholder items for loading state
         * @returns {Array} Array of placeholder items for skeleton loader display
         */
        shimmerItems() {
            return Array.from({ length: 5 }, (_, index) => ({
                __key: index,
                __formatedkey: index,
                _id: index,
                indexno: '',
                data_context: '',
                created_at: '',
                status: '',
                action: '',
            }));
        },

        /**
         * Reactively formats data centre report data for display in the data table
         * @returns {Array} Formatted array of data centre report items
         */
        formattedDataCentreData() {
            return this.getDataCentreReportData.map((item, index) => ({
                __key: index,
                __formatedkey: index,
                _id: item._id,
                indexno: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                serial_number: (this.currentPage - 1) * this.itemsPerPage + index + 1,
                data_context: item.data_context,
                data_type: this.formatDataType(item),
                filter: item.filter,
                period: item.period,
                period_label: this.formatPeriodLabel(item),
                status_date: this.formatStatusDate(item.status_date || item.updated_at),
                updated_at: item.updated_at ? this.formatStatusDate(item.updated_at) : '-',
                period_range: (item.filter?.start_date && item.filter?.end_date) ? `${this.formatToDMY(item.filter.start_date)} - ${this.formatToDMY(item.filter.end_date)}` : ``,
                approval_status: item.filter?.status ?? item.approval_status,
                errormessage: item.errormessage,
                stage: item.stage,
                status: this.getStages(item.stage),
                created_at: item.created_at,
                created_by: item.created_by,
                user_detail: item.user_detail,
                country_detail: item.country_details,
                region_detail: item.region_details,
                period_detail: item.period_detail,
                reportingunit_detail: item.reportingunit_details,
                network_detail: item.network_details,
                module: item.module,
            }));
        },
    },


    beforeDestroy() {
        if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
        // Reset component data to prevent stale state
        this.selectedFilter = {};
        this.cycletype = 'Actual';
    },

    methods: {
        /**
         * Handles page change from Pagination component - fetches data for new page
         * @param {Number} page - New page number (1-based from Pagination component)
         */
        handlePageChange(page) {
            this.currentPage = page; // Update current page state for UI
            this.fetchPageData(page - 1); // API uses 0-based page index
        },

        /**
         * Handles status filter changes from DataTableNew column filter
         * Resets page to 1 and re-fetches with server-side status filtering
         * @param {Object} filters - Filter object from DataTableNew e.g. { status: ['ready', 'failed'] }
         */
        handleTableFilterChange(filters) {
            if (this.isInitialLoad) return; // Skip filter events during initial load
            this.statusFilter = filters.status || [];
            this.currentPage = 1;
            this.fetchPageData(0);
        },

        /**
         * Fetches data for a specific page
         * @param {Number} page - Page number to fetch
         */
        async fetchPageData(page) {
            try {
                await this.fetchDataCentreReportList({
                    page: page,
                    limit: this.itemsPerPage,
                    cycletype: this.cycletype,
                    type: 'data_centre_report',
                    ...(this.statusFilter.length ? { status: this.statusFilter } : {})
                });
            } catch (error) {
                console.error('[DataCentreReport]: Error fetching page data', error);
            }
        },
        getMonthName(monthNumber) {
            if (!monthNumber) return '';
            return MONTHS[monthNumber - 1] || monthNumber;
        },
        downloadFile(id) {
            this.$nova.downloadFile(undefined, {}, REPORTS_API.DOWNLOAD + id)
        },
        showDetails(item) {
            this.detailsDialog = true;
            this.detailsItem = item;
        },

        /**
         * Maps stage codes to their corresponding status variant constants
         * @param {Number} stage - The stage code (0=Generating, 1=Ready, 4=Generating, others=Failed)
         * @returns {String} The stage status variant constant
         */
        getStages(stage) {
            switch (stage) {
                case 0:
                    return STATUS_VARIANTS.GENERATING;
                case 1:
                    return STATUS_VARIANTS.READY;
                case 4:
                    return STATUS_VARIANTS.GENERATING;
                case 2:
                    return STATUS_VARIANTS.FAILED;
                default:
                    return STATUS_VARIANTS.FAILED;
            }
        },
        ...mapActions('varianceanalysis/reports', [
            'fetchDataCentreReportList',
            'fetchDataCentreReportFiltered'
        ]),

        /**
         * Handles filter data changes from GlobalFilter component
         * @param {Object} filterData - Filter data with key-value pairs
         */
        handleFilterData(filterData) {
            // Always update selectedFilter first with incoming data
            if (filterData.key && filterData.value !== undefined) {
                this.$set(this.selectedFilter, filterData.key, filterData.value);
            }

            // Handle initial filter load completion - fetch initial data once filters are ready
            if (filterData.key === 'loading' && filterData.value === false && this.isInitialLoad) {
                this.isInitialLoad = false;
                this.fetchInitialData();
                return;
            }

            // Only fetch filtered data when reload is triggered (Apply button clicked)
            if (filterData.reload) {
                if (this.fetchTimeout) clearTimeout(this.fetchTimeout);

                this.fetchTimeout = setTimeout(() => {
                    this.fetchFilteredData();
                }, 100);
            }
        },

        /**
         * Fetches initial paginated data on component mount
         * API: /v3/varianceanalysis/analysis/datacentrereportlist
         */
        async fetchInitialData() {
            try {
                this.currentPage = 1; // Reset to first page (1-based for UI)
                this.statusFilter = []; // Reset status filter
                await this.fetchDataCentreReportList({ 
                    page: 0, 
                    limit: this.itemsPerPage, 
                    cycletype: this.cycletype, 
                    type: 'data_centre_report' 
                });
            } catch (error) {
                console.error('[DataCentreReport]: Error fetching initial data', error);
            }
        },

        /**
         * Fetches filtered data centre report data
         * API: /v3/varianceanalysis/analysis/datacentrereport
         */
        async fetchFilteredData() {
            try {
                this.filterLoading = true;
                this.currentPage = 1; // Reset to first page on filter apply
                const payload = {
                    batch_type: this.transformToArray(this.selectedFilter.selectedbatchtype),
                    start_date: this.selectedFilter.selectedstartdate || '',
                    end_date: this.selectedFilter.selectedenddate || '',
                    status: this.transformToArray(this.selectedFilter.selecteddatacentrestatus),
                };
                
                await this.fetchDataCentreReportFiltered(payload);
                await this.fetchInitialData();
            } catch (error) {
                console.error('[DataCentreReport]: Error fetching filtered data', error);
            } finally {
                this.filterLoading = false;
            }
        },

        /**
         * Transforms filter value: returns "all" if empty/null, otherwise returns the value
         * @param {String|null} value - Filter value
         * @returns {String} Transformed value
         */
        transformFilterValue(value) {
            return value || 'all';
        },

        /**
         * Transforms filter value to array: returns "all" if empty/null, otherwise returns array
         * Used for multi-select fields like network, region, country
         * @param {String|Array|null} value - Filter value
         * @returns {String|Array} Transformed value as array or 'all'
         */
        transformToArray(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        /**
         * Transforms reporting unit value: returns "all" if empty or 'all' string, otherwise returns array
         * @param {String|Array|null} value - Reporting unit value
         * @returns {String|Array} Transformed value
         */
        transformReportingUnit(value) {
            if (!value || value === 'all' || (Array.isArray(value) && value.length === 0)) {
                return 'all';
            }
            return Array.isArray(value) ? value : [value];
        },

        formatBatchTypes(requestTypes) {
            if (!requestTypes) return '';
            const types = Array.isArray(requestTypes) ? requestTypes : [requestTypes];
            return types
                .map(typeId => FILTER_BATCH_TYPE_LIST[typeId]?.name || typeId)
                .join(', ');
        },

        formatDataType(item) {
            if (item.data_type) return item.data_type;
            const types = item.filter?.request_type;
            if (!types) return '';
            const arr = Array.isArray(types) ? types : [types];
            return arr
                .map(typeId => FILTER_BATCH_TYPE_LIST[typeId]?.name || typeId)
                .join(', ');
        },

        formatPeriodLabel(item) {
            if (item.period_label) return item.period_label;
            const ru = item.reportingunit_details;
            if (ru && Array.isArray(ru) && ru.length > 0 && ru[0]?.code) {
                return `${ru[0].code} - ${ru[0].name}`;
            }
            if (item.period_detail && typeof item.period_detail === 'object') {
                return `${item.period_detail.year || ''} - ${this.getMonthName(item.period_detail.number) || ''}`;
            }
            return item.period_detail || '';
        },

        formatToDMY(dateString) {
            if (!dateString) return '';
            const [year, month, day] = dateString.split('-');
            return `${day}/${month}/${year}`;
        },

        formatStatusDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const day = date.getDate();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${day} ${months[date.getMonth()]}, ${date.getFullYear()}`;
        },

        getApprovalStatusLabel(status) {
            if (typeof status === 'string') return status;
            const entry = FILTER_DATA_CENTRE_STATUS_LIST[status];
            if (entry) return entry.name;
            if (status === 1) return 'In Progress';
            if (status === 2) return 'Complete';
            if (status === -1) return 'Failed';
            return 'Unknown';
        },

        getApprovalStatusColor(status) {
            if (status === 1) return 'var(--workflow-yellow-regional)';
            if (status === 2) return 'var(--workflow-green)';
            if (status === -1) return 'var(--workflow-red-local)';
            return 'grey lighten-2';
        },

        getApprovalStatusTextColor() {
            return 'white';
        },
    },
};
</script>

<style scoped>
.failed-reason {
    color: var(--status-red);
}
</style>
